/* eslint-disable no-restricted-properties */
/* eslint-disable no-restricted-globals */
import { cloneDeep } from 'lodash';

export const hasDuplicates = (array) => {
  const lowerCaseArray = array.map((element) => element.toLowerCase());
  const uniqueSet = new Set(lowerCaseArray);
  return uniqueSet.size !== array.length;
};

export const getDuplicates = (array) => {
  return [...new Set(array.filter((elem, idx, arr) => arr.indexOf(elem) !== idx))];
};

export const getUniqueItems = (data) => {
  const values = typeof data === 'string' ? data.split(',') : data;
  const nonEmptyValues = values.filter((item) => item);
  const uniqueValues = [...new Set(nonEmptyValues)];
  return uniqueValues;
};

export const getProductVitals = (product) => {
  const optionsWithoutValues = product?.options.filter(({ values }) => !values.filter((item) => item).length);
  const anyOptionLacksValues = !!optionsWithoutValues?.length;

  const labels = product?.options.map(({ label }) => label).filter((item) => item) || [];
  const optionValues = product?.options.flatMap(({ values }) => values) || [];
  const anyOptionLacksLabel = product?.options.some(({ label }) => !label);
  const isIncompleteOptions = anyOptionLacksLabel || anyOptionLacksValues;
  const isMultipleOptions = labels.length > 1;

  return {
    optionsWithoutValues,
    anyOptionLacksLabel,
    isIncompleteOptions,
    isMultipleOptions,
    optionValues,
    labels,
  };
};

export const parsedInteger = (input) => {
  const parsedValue = parseInt(input, 10);
  if (Number.isNaN(parsedValue)) return undefined;
  return parsedValue;
};

export const transformProducts = {
  toFrontEndShape: (products, isEditable = false) => {
    return cloneDeep(products).map((product) => {
      const { variants, options } = product;
      const newProduct = product;

      const getCsvCombination = (combination) =>
        Object.values(options)
          .map(({ label }) => combination[label] || '')
          .join(',');

      if (Object.keys(variants).length) {
        newProduct.variants = Object.entries(variants).map(([id, { combination, quantity }]) => ({
          combination: isEditable ? getCsvCombination(combination) : combination,
          quantity,
          id,
        }));
      } else {
        newProduct.variants = [];
      }

      if (Object.keys(options).length) {
        newProduct.options = Object.entries(options).map(([id, { label, values }]) => ({ id, label, values }));
      } else {
        newProduct.options = [];
      }

      return newProduct;
    });
  },

  toBackEndShape: (products, isEdited = false) => {
    return cloneDeep(products).map((product) => {
      const { variants, options } = product;
      const newProduct = product;

      if (Array.isArray(variants) && variants.length) {
        const labels = options.map(({ label }) => label);

        newProduct.variants = variants.reduce((acc, variant) => {
          const combination = !isEdited ? variant.combination : {};
          const { id, quantity } = variant;

          if (isEdited) {
            labels.forEach((label, index) => {
              const optionValue = variant.combination.split(',')[index];
              if (optionValue) combination[label] = optionValue;
            });
          }

          return { ...acc, [id]: { combination, quantity } };
        }, {});
      } else if (typeof variants === 'object' && !Array.isArray(variants)) {
        newProduct.variants = variants;
      } else {
        newProduct.variants = {};
      }

      if (Array.isArray(options) && options.length) {
        newProduct.options = options.reduce((acc, { id, label, values }) => {
          return { ...acc, [id]: { label, values } };
        }, {});
      } else if (typeof options === 'object' && !Array.isArray(options)) {
        newProduct.options = options;
      } else {
        newProduct.options = {};
      }

      return newProduct;
    });
  },
};

/**
 *  Generates a unique identifier of specified length
 *  @param  {Array} existingIds - Array of existing unique id strings.
 *  @param  {Number}  maxLength - Maximum length of id digits to be generated.
 *  @return {Number}  uniqueId
 */
export const getUniqueId = (existingIds = [], maxLength = 4) => {
  // const isValidUniqueId = (uniqueId) => existingIds.every((id) => uniqueId > id) && !existingIds.includes(uniqueId);
  // const max = Math.pow(10, maxLength) - 1;
  const min = Math.pow(10, maxLength - 1);
  if (!existingIds.length) return min;

  const largestId = Number(Math.max(...existingIds));
  return largestId + 1;

  // uniqueId = Math.floor(Math.random() * (max - min + 1)) + min;
  // if (isValidUniqueId(uniqueId)) return uniqueId;

  // return getUniqueId(existingIds, maxLength);
};

/**
 *  Validate CSV body data against a set of header
 *  @param  {String | Array}  csvData - New line separated strings of values | Array of comma separated single string arrays.
 *  @param  {Array} csvHeader
 *  @return {Object}  response
 */
export const validateCSVBody = (csvData, csvHeader) => {
  const newVariants = typeof csvData === 'string' ? csvData.split('\n') : csvData;
  const response = { message: '', data: [] };

  newVariants.forEach((combination, index) => {
    const variant = typeof csvData === 'string' ? combination.split(',') : combination;
    const genericMessage = `Exactly ${csvHeader.length} values required.`;
    const fieldsWithValue = variant.filter((value) => value).length;
    const isValidFieldsLength = variant.length === csvHeader.length;
    const lastIndex = variant.length - 1;
    const quantity = variant[lastIndex];
    const lineNumber = index + 1;

    // Only valid entry point
    if (isValidFieldsLength && fieldsWithValue >= 2 && quantity) response.data.push(variant);

    if (!isValidFieldsLength) {
      const hasExcessFieldsLength = variant.length > csvHeader.length;
      const lineHasDelimitersOnly = variant.length > 1 && !hasExcessFieldsLength; //  Just comma/commas
      const isSingleFieldWithValue = variant.length === 1 && fieldsWithValue; //  Just a value without delimiter

      if (lineHasDelimitersOnly || isSingleFieldWithValue) {
        response.message = `Incomplete CSV values on line ${lineNumber}. ${genericMessage}`;
      }

      if (hasExcessFieldsLength) {
        response.message = `CSV entry on line ${lineNumber} has excess values. ${genericMessage}`;
      }
    }

    if (isValidFieldsLength) {
      if (fieldsWithValue === 1 && quantity) {
        response.message = `At least one variant option is required on line ${lineNumber}`;
      }

      if (!quantity) {
        response.message = `CSV quantity value is required on line ${lineNumber}.`;
      } else if (parsedInteger(quantity) === undefined) {
        response.message = `CSV quantity value on line ${lineNumber} is not a number.`;
      }
    }
  });

  return response;
};

/**
 *  Generates CSV values from product variants/options array
 *  @param  {Object}  product
 *  @return {String}
 */
export const generateVariantsCSVs = (product) => {
  const labels = product.options.map(({ label }) => label).filter((item) => item);
  const line = [...labels.map((i) => ''), 0];

  if (product.variants.length) {
    const quantityIndex = labels.length;

    const lines = product.variants.map(({ combination, quantity }) => {
      const variant = Object.values(combination);
      const newLine = [...line];

      line.forEach((item, index) => {
        if (index !== quantityIndex) {
          newLine[index] = variant[index];
        }
      });

      if (quantity) newLine[quantityIndex] = quantity;
      return newLine;
    });

    return lines.join('\n');
  }

  return '';
};

/**
 *  Generates stock data objects(options and variants) from csv array
 *  @param  {Array}  csvData
 *  @return {Object}  { message, options, variants }
 */
export const generateStockDataFromCSV = (csvData) => {
  let labels = csvData.flatMap((item) => Object.keys(item).filter((key) => key.toLowerCase() !== 'quantity'));
  labels = [...new Set(labels)];
  const variants = [];
  let message = '';

  const options = labels.map((label) => ({ label, values: [] }));

  csvData.forEach((output) => {
    const variant = { combination: {}, quantity: 0 };

    Object.entries(output).forEach(([key, value]) => {
      if (key.toLowerCase() === 'quantity') {
        variant.quantity = value;
      } else {
        variant.combination[key] = value;

        const option = options.find(({ label }) => label === key);
        if (value && !option.values.includes(value)) {
          option.values.push(value);
        }
      }
    });

    variants.push(variant);
  });

  const csvHeader = [...labels, 'quantity'];
  const variantsArray = variants.map(({ combination, quantity }) => {
    const variant = Object.values(combination);
    return [...variant, quantity];
  });

  const validation = validateCSVBody(variantsArray, csvHeader);
  message = validation.message;

  return { message, options, variants };
};

/**
 *  Generates an array of csv variants from product variants and option labels
 *  @param  {Array}  variants
 *  @param  {Array}  labels
 *  @return {Array}
 */
export const getCsvVariants = (variants, labels) => {
  const csvVariants = variants.map(({ combination, quantity }) => {
    const optionValues = combination.split(',');
    const variant = [];

    labels.forEach((label, index) => {
      variant[index] = optionValues[index] || '';
      variant.push(quantity);
    });

    return variant;
  });

  return csvVariants.join('\n');
};

export const getTrimmedValues = (data) => data.map((item) => item.trim());
